import { SUPABASE_URL } from '../utils/supabase/url';
/**
 * Google Maps & Places API Integration
 * 
 * Provides address autocomplete, geocoding, and location services
 * Updated to use new Places API (AutocompleteSuggestion) as of March 2025
 */

import { projectId } from '../utils/supabase/info';
import { getSupabaseClient } from '../utils/supabase/client';
import { getAuthHeaders } from '../utils/supabase/client';
import {
  logEdgeTransportIssueOnce,
  markEdgeBackendAvailable,
  markEdgeBackendUnavailable,
  shouldAttemptEdgeRequest,
} from '../utils/edgeNetworkState';

// Cache for API key
let cachedApiKey: string | null = null;
let fetchPromise: Promise<string> | null = null;

const MAPS_KEY_STORAGE_KEY = 'quantis-gmaps-key';
const MAPS_KEY_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function readCachedApiKeyFromStorage(): string | null {
  try {
    const raw = sessionStorage.getItem(MAPS_KEY_STORAGE_KEY);
    if (!raw) return null;
    const { key, ts } = JSON.parse(raw) as { key: string; ts: number };
    if (!key || Date.now() - ts > MAPS_KEY_TTL_MS) {
      sessionStorage.removeItem(MAPS_KEY_STORAGE_KEY);
      return null;
    }
    return key;
  } catch {
    return null;
  }
}

function writeCachedApiKeyToStorage(key: string): void {
  try {
    sessionStorage.setItem(MAPS_KEY_STORAGE_KEY, JSON.stringify({ key, ts: Date.now() }));
  } catch {
    // Ignore storage quota errors
  }
}
const UK_IE_BOUNDS = {
  south: 49.8,
  west: -10.9,
  north: 60.95,
  east: 1.9,
} as const;

const toSafeConsoleArg = (arg: unknown): unknown => {
  if (arg == null) return arg;
  if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean' || typeof arg === 'bigint') {
    return arg;
  }
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof Event !== 'undefined' && arg instanceof Event) return `[Event ${arg.type}]`;
  if (typeof arg === 'object') {
    const ctor = (arg as { constructor?: { name?: string } })?.constructor?.name || 'Object';
    return `[${ctor}]`;
  }
  return String(arg);
};

const forwardConsoleSafely = (logFn: (...args: any[]) => void, args: any[]): void => {
  try {
    logFn.apply(console, args);
  } catch {
    try {
      logFn.apply(console, args.map(toSafeConsoleArg));
    } catch {
      // Ignore logger failures to avoid breaking app control flow
    }
  }
};

/**
 * Current session access token, or null when signed out.
 *
 * Deliberately not `getAuthHeaderToken()`: that helper force-expires a dead
 * session and bounces to login, which is far too heavy a side effect for a
 * best-effort Maps key lookup that several screens fire on mount.
 */
async function getSessionAccessToken(): Promise<string | null> {
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    return data.session?.access_token || null;
  } catch {
    return null;
  }
}

/**
 * Fetch Google Maps API key from backend
 */
async function fetchApiKeyFromBackend(): Promise<string> {
  // Return existing promise if fetch is already in progress
  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    const url = `${SUPABASE_URL}/functions/v1/make-server-a476273b/google-maps-api-key`;
    if (!shouldAttemptEdgeRequest(url)) {
      fetchPromise = null;
      return '';
    }

    try {
      // This route is session-guarded on the server and rejects the anon key
      // outright (`token === anonKey` -> 401), so sending `publicAnonKey` here
      // made every key fetch 401 and left Maps, geocoding and the site-weather
      // panel silently running without a key. Send the signed-in user's access
      // token instead, and skip the round-trip entirely when there is no
      // session - nothing pre-auth needs the Maps key.
      const token = await getSessionAccessToken();
      if (!token) {
        fetchPromise = null;
        return '';
      }

      // The route requires a user session (getRequestAccessContext guard on
      // the server) — the anon key gets a 401. Resolve headers before
      // starting the abort timer so a slow token refresh can't eat the
      // fetch's 5s budget.
      const headers = await getAuthHeaders();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(url, {
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // Don't let this fail silently again: a rejected token here disables
          // every Maps-backed feature at once.
          console.warn(
            `[Google Maps] API key request rejected (HTTP ${response.status}). ` +
            `The session token was not accepted by /google-maps-api-key - ` +
            `Maps, geocoding and site weather will be disabled this session.`,
          );
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const apiKey = data.apiKey || '';

      if (!apiKey) {
        console.warn('⚠️ [Google Maps] API key is empty - Google Maps functionality will be disabled');
      }

      markEdgeBackendAvailable();
      cachedApiKey = apiKey;
      fetchPromise = null;
      return apiKey;
    } catch (error) {
      markEdgeBackendUnavailable(error);
      logEdgeTransportIssueOnce('Google Maps', error);
      fetchPromise = null;
      return '';
    }
  })();

  return fetchPromise;
}

/** Synchronously returns the API key if already cached in memory or sessionStorage, otherwise null. */
export function getCachedGoogleMapsApiKey(): string | null {
  if (cachedApiKey) return cachedApiKey;
  const stored = readCachedApiKeyFromStorage();
  if (stored) {
    cachedApiKey = stored;
    return cachedApiKey;
  }
  return null;
}

export async function getGoogleMapsApiKey(): Promise<string> {
  // Return in-memory cached key if available
  if (cachedApiKey) {
    return cachedApiKey;
  }

  // Try environment variable first
  try {
    // @ts-ignore - import.meta is Vite-specific
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GOOGLE_MAPS_API_KEY) {
      // @ts-ignore
      cachedApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;
      return cachedApiKey;
    }
  } catch (error) {
    // Ignore access errors in non-Vite environments
  }

  // Try window object
  if (typeof window !== 'undefined' && (window as any).GOOGLE_MAPS_API_KEY) {
    cachedApiKey = (window as any).GOOGLE_MAPS_API_KEY;
    return cachedApiKey!;
  }

  // Check sessionStorage cache (avoids edge function round-trip on every session)
  const stored = readCachedApiKeyFromStorage();
  if (stored) {
    cachedApiKey = stored;
    return cachedApiKey;
  }

  // Fetch from backend and persist to sessionStorage
  cachedApiKey = await fetchApiKeyFromBackend();
  if (cachedApiKey) {
    writeCachedApiKeyToStorage(cachedApiKey);
  }
  return cachedApiKey;
}

export interface PlaceAutocompleteResult {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export interface PlaceDetails {
  formatted_address: string;
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

export interface GeocodedAddress {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  postcode: string;
  townCity: string;
  county: string;
  country: string;
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

class GoogleMapsServiceClass {
  private geocoder: google.maps.Geocoder | null = null;
  private isLoaded = false;
  private errorHandlerInstalled = false;
  private loadPromise: Promise<void> | null = null;

  /** Synchronously returns true once the Maps script has fully loaded. */
  get isApiReady(): boolean {
    return this.isLoaded;
  }

  constructor() {
    // Suppress benign CSS SecurityError from Google Maps
    this.suppressCSSSecurityErrors();
  }

  /**
   * Suppress harmless CSS access errors from Google Maps
   */
  private suppressCSSSecurityErrors(): void {
    if (this.errorHandlerInstalled || typeof window === 'undefined') return;

    const originalError = console.error;
    console.error = (...args: any[]) => {
      // Suppress the specific CSS SecurityError that Google Maps generates
      const firstArg = args[0];
      const errorMessage = firstArg instanceof Error ? firstArg.message : (firstArg?.toString() || '');
      
      if (
        errorMessage.includes('cssRules') ||
        errorMessage.includes('CSSStyleSheet') ||
        (errorMessage.includes('SecurityError') && errorMessage.includes('stylesheet')) ||
        errorMessage.includes('Could not access stylesheet') ||
        // Also suppress the multiple API inclusion warning if we catch it late
        errorMessage.includes('included the Google Maps JavaScript API multiple times')
      ) {
        // Silently ignore this benign error
        return;
      }
      // Pass through all other errors
      forwardConsoleSafely(originalError, args);
    };

    this.errorHandlerInstalled = true;
  }

  /**
   * Load Google Maps JavaScript API with async loading pattern
   */
  async loadGoogleMaps(): Promise<void> {
    // Return existing promise if loading is already in progress
    if (this.loadPromise) {
      return this.loadPromise;
    }

    if (this.isLoaded) return;

    this.loadPromise = (async () => {
      const apiKey = await getGoogleMapsApiKey();

      if (!apiKey) {
        this.loadPromise = null;
        throw new Error('Google Maps API key is not configured.');
      }

      // Check if Google Maps is already available in global scope
      if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
        this.isLoaded = true;
        this.initializeServices();
        return;
      }

      // Check if script is already in the DOM but maybe not loaded yet
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existingScript) {
        // If script exists, wait for it to load
        await new Promise<void>((resolve) => {
          const checkGoogleMaps = () => {
            if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
              this.isLoaded = true;
              this.initializeServices();
              resolve();
            } else {
              // Retry after a short delay
              setTimeout(checkGoogleMaps, 100);
            }
          };
          checkGoogleMaps();
        });
        return;
      }

      // Load the script
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&loading=async&region=GB`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
          // Wait a bit for Google Maps to fully initialize
          const checkGoogleMaps = () => {
            if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
              this.isLoaded = true;
              this.initializeServices();
              resolve();
            } else {
              // Retry after a short delay
              setTimeout(checkGoogleMaps, 50);
            }
          };
          checkGoogleMaps();
        };

        script.onerror = () => {
          this.loadPromise = null; // Reset promise on failure so we can try again
          reject(new Error('Failed to load Google Maps API'));
        };

        document.head.appendChild(script);
      });
    })();

    return this.loadPromise;
  }

  /**
   * Initialize Google Maps services
   */
  private initializeServices(): void {
    if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
      console.warn('Google Maps not fully loaded yet');
      return;
    }

    try {
      this.geocoder = new google.maps.Geocoder();
    } catch (error) {
      console.error('Failed to initialize Geocoder:', error);
    }
  }

  /**
   * Get address autocomplete suggestions (UK + Ireland focused)
   * Updated to use new AutocompleteSuggestion API
   */
  async getAddressSuggestions(input: string): Promise<PlaceAutocompleteResult[]> {
    if (!this.isLoaded) {
      await this.loadGoogleMaps();
    }

    try {
      // Use the new AutocompleteSuggestion API
      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['GB', 'IE'],
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'postal_code', 'route'],
        locationRestriction: UK_IE_BOUNDS,
      });

      const ukPostcodePattern =
        /\b[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}\b/i;
      const irishEircodePattern =
        /\b[A-Z]\d{2}\s?[A-Z0-9]{4}\b/i;

      const normalized = suggestions
        .map((suggestion) => ({
          description: suggestion.placePrediction?.text?.text || '',
          place_id: suggestion.placePrediction?.placeId || '',
          structured_formatting: {
            main_text: suggestion.placePrediction?.mainText?.text || suggestion.placePrediction?.text?.text || '',
            secondary_text: suggestion.placePrediction?.secondaryText?.text || '',
          },
        }))
        .filter((suggestion) => suggestion.place_id && suggestion.description);

      const scoreSuggestion = (suggestion: PlaceAutocompleteResult): number => {
        const description = suggestion.description;
        const mainText = suggestion.structured_formatting.main_text;
        const secondaryText = suggestion.structured_formatting.secondary_text;
        const segmentCount = description.split(',').map((part) => part.trim()).filter(Boolean).length;
        const hasNumericBuilding = /\b\d+[A-Z]?\b/i.test(mainText);
        const hasUkPostcode =
          ukPostcodePattern.test(description) || ukPostcodePattern.test(secondaryText);
        const hasIrishEircode =
          irishEircodePattern.test(description) || irishEircodePattern.test(secondaryText);
        const hasPostalCode = hasUkPostcode || hasIrishEircode;
        const looksLikeRouteOnly =
          !hasNumericBuilding &&
          !hasPostalCode &&
          segmentCount <= 2 &&
          mainText.length < 28;
        let score = 0;

        if (hasUkPostcode) score += 8;
        if (hasIrishEircode) score += 8;
        if (hasNumericBuilding) score += 4;
        if (secondaryText.includes(',')) score += 2;
        if (segmentCount >= 3) score += 3;
        if (segmentCount >= 4) score += 2;
        if (secondaryText.length > 18) score += 1;
        if (!secondaryText) score -= 3;
        if (looksLikeRouteOnly) score -= 8;
        if (/london|manchester|birmingham|glasgow|leeds|bristol|liverpool|sheffield|edinburgh|cardiff|belfast/i.test(description)) {
          score += 1;
        }
        score += Math.min(description.length, 120) / 120;
        return score;
      };

      return normalized
        .sort((a, b) => scoreSuggestion(b) - scoreSuggestion(a))
        .filter((suggestion, index) => index < 8 || scoreSuggestion(suggestion) > -2);
    } catch (error) {
      console.error('Autocomplete failed:', error);
      return [];
    }
  }

  /**
   * Get detailed place information by place ID
   * Updated to use new Place API
   */
  async getPlaceDetails(placeId: string): Promise<PlaceDetails> {
    if (!this.isLoaded) {
      await this.loadGoogleMaps();
    }

    try {
      // Use the new Place API
      const { Place } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
      
      const place = new Place({
        id: placeId,
      });

      // Fetch required fields
      await place.fetchFields({
        fields: ['formattedAddress', 'addressComponents', 'location'],
      });

      // Convert to legacy format for compatibility
      const addressComponents = place.addressComponents?.map((component) => ({
        long_name: component.longText || '',
        short_name: component.shortText || '',
        types: component.types || [],
      })) || [];

      return {
        formatted_address: place.formattedAddress || '',
        address_components: addressComponents,
        geometry: {
          location: {
            lat: place.location?.lat() || 0,
            lng: place.location?.lng() || 0,
          },
        },
      };
    } catch (error) {
      console.error('Place details failed:', error);
      throw new Error(`Place details failed: ${error}`);
    }
  }

  /**
   * Parse Google place details into structured address
   */
  parseAddressComponents(placeDetails: PlaceDetails, fallbackAddressText = ''): GeocodedAddress {
    const components = placeDetails.address_components;
    
    let streetNumber = '';
    let route = '';
    let premises = '';
    let subpremise = '';
    let establishment = '';
    let locality = '';
    let postalTown = '';
    let sublocality = '';
    let adminAreaLevel1 = '';
    let adminArea = '';
    let postcode = '';
    let postcodeSuffix = '';
    let country = '';

    components.forEach((component) => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('premise')) {
        premises = component.long_name;
      }
      if (types.includes('subpremise')) {
        subpremise = component.long_name;
      }
      if (types.includes('establishment')) {
        establishment = component.long_name;
      }
      if (types.includes('locality')) {
        locality = component.long_name;
      }
      if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
        sublocality = component.long_name;
      }
      if (types.includes('postal_town')) {
        postalTown = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        adminAreaLevel1 = component.long_name;
      }
      if (types.includes('administrative_area_level_2')) {
        adminArea = component.long_name;
      }
      if (types.includes('postal_code')) {
        postcode = component.long_name;
      }
      if (types.includes('postal_code_suffix')) {
        postcodeSuffix = component.long_name;
      }
      if (types.includes('country')) {
        country = component.long_name;
      }
    });

    const postcodeSource = [fallbackAddressText, placeDetails.formatted_address]
      .filter(Boolean)
      .join(' | ');
    const ukPostcodeMatch = postcodeSource.match(
      /\b([A-Z]{1,2}\d[A-Z\d]?)\s?(\d[A-Z]{2})\b/i,
    );
    const formattedUkPostcode = ukPostcodeMatch
      ? `${ukPostcodeMatch[1].toUpperCase()} ${ukPostcodeMatch[2].toUpperCase()}`
      : '';
    const normalizedPostcode = formattedUkPostcode || postcode || postcodeSuffix || '';
    const townCity = postalTown || locality || sublocality;
    const county = adminArea || adminAreaLevel1;

    let addressLine1 = [streetNumber, route].filter(Boolean).join(' ').trim();
    let addressLine2 = [subpremise, premises || establishment].filter(Boolean).join(', ').trim();

    if (!addressLine1 && premises) {
      addressLine1 = premises;
      addressLine2 = [subpremise, route].filter(Boolean).join(', ').trim();
    }

    if (!addressLine1 && establishment) {
      addressLine1 = establishment;
      addressLine2 = [subpremise, route].filter(Boolean).join(', ').trim();
    }

    if (!addressLine1 && placeDetails.formatted_address) {
      const [firstLine = '', secondLine = ''] = placeDetails.formatted_address.split(',').map((part) => part.trim());
      addressLine1 = firstLine;
      if (!addressLine2 && secondLine) {
        addressLine2 = secondLine;
      }
    }

    const addressLine3 = [townCity, county].filter(Boolean).join(', ');

    return {
      addressLine1: addressLine1 || '',
      addressLine2: addressLine2 || '',
      addressLine3: addressLine3 || '',
      postcode: normalizedPostcode || '',
      townCity: townCity || '',
      county: county || '',
      country: country || 'United Kingdom',
      latitude: placeDetails.geometry.location.lat,
      longitude: placeDetails.geometry.location.lng,
      formattedAddress: placeDetails.formatted_address,
    };
  }

  /**
   * Geocode an address string to get coordinates
   */
  async geocodeAddress(address: string): Promise<{ lat: number; lng: number }> {
    if (!this.isLoaded) {
      await this.loadGoogleMaps();
    }

    if (!this.geocoder) {
      throw new Error('Geocoder not initialized');
    }

    return new Promise((resolve, reject) => {
      this.geocoder!.geocode(
        {
          address,
          region: 'GB',
          bounds: new google.maps.LatLngBounds(
            new google.maps.LatLng(UK_IE_BOUNDS.south, UK_IE_BOUNDS.west),
            new google.maps.LatLng(UK_IE_BOUNDS.north, UK_IE_BOUNDS.east),
          ),
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
            const location = results[0].geometry.location;
            resolve({
              lat: location.lat(),
              lng: location.lng(),
            });
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        }
      );
    });
  }

  /**
   * Calculate distance between two coordinates (in miles)
   */
  calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 3958.8; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) *
        Math.cos(this.toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return Math.round(distance * 10) / 10; // Round to 1 decimal place
  }

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Format distance for display
   */
  formatDistance(miles: number): string {
    if (miles < 1) {
      const feet = Math.round(miles * 5280);
      return `${feet} ft`;
    }
    return `${miles.toFixed(1)} miles`;
  }
}

// Export singleton instance
export const GoogleMapsService = new GoogleMapsServiceClass();
